import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ErrorPage403 from '@/app/routes/errorPage403';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import { Pagination, Column, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import {
  ColumnCustomizationPopup,
  type ColumnOption,
} from '@shared/components/ColumnCustomizationPopup';
import { ChipsWithMore } from '@shared/components/Chip';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Tooltip } from '@shared/components/Tooltip';
import { Snackbar } from '@shared/components/Snackbar';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import { OfferUpdateStatusDialog } from '../../components/OfferUpdateStatusDialog';
import { useOfferList } from '../../hooks/useOfferList';
import { updateOfferStatusApiService } from '../../api/updateOfferStatusApiService';
import type { OfferListItem } from '../../types/offerListType';
import {
  formatValidityPeriod,
  getDiscountTypeLabel,
  formatDiscountValue,
} from '../../utils/offerListFormatters';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { OFFER_ERROR_MESSAGES } from '../../errors/errorMessages';
import noDataAnimation from '@/assets/lottie/noDataAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import {
  getOfferNameColumnWidthPx,
  defaultOfferNameColumnWidthPx,
  baseViewportWidth,
  minSpaceBelowForMenu,
  offerNameMaxLength,
} from '../../config/offerListLayout';

const OFFER_COLUMN_OPTIONS: ColumnOption[] = [
  { key: 'offerName', label: 'Imię i nazwisko', disabled: true },
  { key: 'discountId', label: 'Identyfikator rabatu', disabled: true },
  { key: 'subscriptionPlans', label: 'Nazwa planu', disabled: true },
  { key: 'discountType', label: 'Typ rabatu' },
  { key: 'discountValue', label: 'Wartość rabatu' },
  { key: 'validityPeriod', label: 'Okres obowiązywania', disabled: true },
  { key: 'status', label: 'Status', disabled: true },
];

const DEFAULT_VISIBLE_OFFER_COLUMNS: Record<string, boolean> =
  Object.fromEntries(OFFER_COLUMN_OPTIONS.map((o) => [o.key, true]));

const COLUMNS_URL_PARAM = 'cols';

/** Comma-separated list of visible column keys when all are visible. */
const DEFAULT_COLS_STRING = OFFER_COLUMN_OPTIONS.map((o) => o.key).join(',');

function parseColsParam(value: string | null): Record<string, boolean> | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const validKeys = new Set(OFFER_COLUMN_OPTIONS.map((o) => o.key));
  const visibleKeys = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => validKeys.has(s));
  if (visibleKeys.length === 0) {
    return null;
  }
  const result: Record<string, boolean> = {};
  OFFER_COLUMN_OPTIONS.forEach((o) => {
    result[o.key] = visibleKeys.includes(o.key);
  });
  return result;
}

function buildColsParam(visibleKeys: Record<string, boolean>): string {
  return OFFER_COLUMN_OPTIONS.filter((o) => visibleKeys[o.key] !== false)
    .map((o) => o.key)
    .join(',');
}

function OfferList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnToListSearch = location.search;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnPopupOpen, setIsColumnPopupOpen] = useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const columnContainerRef = useRef<HTMLDivElement | null>(null);

  const [offerNameColumnWidthPx, setOfferNameColumnWidthPx] = useState(() =>
    typeof window !== 'undefined'
      ? getOfferNameColumnWidthPx(window.innerWidth)
      : defaultOfferNameColumnWidthPx
  );
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : baseViewportWidth
  );
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setViewportWidth(w);
      setOfferNameColumnWidthPx(getOfferNameColumnWidthPx(w));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<OfferListItem | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] = useState(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState('');

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<
    Record<string, boolean>
  >(() => ({ ...DEFAULT_VISIBLE_OFFER_COLUMNS }));

  // Restore column visibility from URL on load / when cols param changes (e.g. back/forward, sidenav reset)
  const colsParam = searchParams.get(COLUMNS_URL_PARAM);
  useEffect(() => {
    const parsed = parseColsParam(colsParam);
    if (parsed) {
      setVisibleColumnKeys(parsed);
    } else {
      // No cols in URL = default state (e.g. sidenav reset to /Oferty?page=1&limit=10)
      setVisibleColumnKeys({ ...DEFAULT_VISIBLE_OFFER_COLUMNS });
    }
  }, [colsParam]);

  const {
    offers,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    debouncedSearch,
    isSearchClearing,
    setSearch,
    statusOptions,
    setStatusOptions,
    dateRange,
    setDateRange,
    subscriptionPlanOptions,
    setSubscriptionPlanOptions,
    subscriptionPlansLoading,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetchOffers,
    handleSort,
    sortBy,
    sortOrder,
    accessError,
  } = useOfferList();

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  // Close filter/column popups when user clicks "Oferty" in side nav (same page)
  useEffect(() => {
    const handleClosePopups = () => {
      setIsFilterOpen(false);
      setIsColumnPopupOpen(false);
      setOpenMenuRowId(null);
    };
    window.addEventListener('offer-list-close-popups', handleClosePopups);
    return () =>
      window.removeEventListener('offer-list-close-popups', handleClosePopups);
  }, []);

  const prevOpenMenuRowIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (openMenuRowId !== null) {
      prevOpenMenuRowIdRef.current = openMenuRowId;
      const anchor = menuAnchorRef.current;
      if (anchor) {
        const firstMenuitem =
          anchor.querySelector<HTMLElement>('[role="menuitem"]');
        if (firstMenuitem) {
          requestAnimationFrame(() => firstMenuitem.focus());
        }
      }
    } else {
      const hadOpen = prevOpenMenuRowIdRef.current !== null;
      prevOpenMenuRowIdRef.current = null;
      if (hadOpen && menuTriggerRef.current) {
        requestAnimationFrame(() => menuTriggerRef.current?.focus());
      }
    }
  }, [openMenuRowId]);

  const filterCount =
    appliedFilters.statusOptions.filter((o) => o.checked).length +
    (appliedFilters.dateRange.startDate || appliedFilters.dateRange.endDate
      ? 1
      : 0) +
    appliedFilters.subscriptionPlanOptions.filter((o) => o.checked).length;

  const columns: Column<OfferListItem>[] = useMemo(
    () => [
      {
        key: 'offerName',
        header: 'Imię i nazwisko',
        minWidth: '120px',
        //   viewportWidth > baseViewportWidth
        //     ? `${baseOfferNameColumnWidthPx}px`
        //     : `${offerNameColumnWidthPx}px`,
        // ...(viewportWidth <= baseViewportWidth && {
        //   width: `${offerNameColumnWidthPx}px`,
        // }),
        showSortIcon: true,
        render: (row) => {
          const name = row.offerName ?? '';
          const displayText =
            name.length > offerNameMaxLength
              ? `${name.slice(0, offerNameMaxLength)}...`
              : name;
          const isTruncated = name.length > offerNameMaxLength;
          return (
            <div className="w-full min-w-0 overflow-hidden">
              <Tooltip
                content={name}
                forceShow={isTruncated}
                contentMaxWidth={480}
                useContentWidth={name.length > 50}
              >
                <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block min-w-0 w-full">
                  {displayText}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        key: 'discountId',
        header: 'Identyfikator rabatu',
        width: '190px',
        // noTruncate: true,
        render: (row) => (
          <Tooltip content={row.discountId}>
            <span
              className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block"
              style={{ maxWidth: '128px' }}
            >
              {row.discountId}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'subscriptionPlans',
        header: 'Nazwa planu',
        minWidth: '240px',
        render: (row) => (
          <ChipsWithMore
            items={row.subscriptionPlans ?? []}
            maxVisible={1}
            moreLabel="więcej"
            emptyPlaceholder="—"
            tooltipMaxWidth={560}
            className="cursor-pointer"
          />
        ),
      },
      {
        key: 'discountType',
        header: 'Typ rabatu',
        width: '150px',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block min-w-0">
            {getDiscountTypeLabel(row.discountType)}
          </span>
        ),
      },
      {
        key: 'discountValue',
        header: 'Wartość rabatu',
        width: '160px',
        // noTruncate: true,
        render: (row) => (
          <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block min-w-0">
            {formatDiscountValue(row.discountType, row.discountValue)}
          </span>
        ),
      },
      {
        key: 'validityPeriod',
        header: 'Okres obowiązywania',
        minWidth: '240px',
        showSortIcon: true,
        // noTruncate: true,
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px]">
            {formatValidityPeriod(row.startDate, row.endDate)}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: '184px',
        // noTruncate: true,
        render: (row, index) => {
          const canToggle =
            privileges.offerActivateDeactivate && !row.isExpired;
          const statusToggle = (
            <div
              data-testid={`toggle-status-${index}`}
              onClick={(e) => {
                if (!canToggle) {
                  return;
                }
                e.stopPropagation();
                setSelectedOffer(row);
                setIsDialogOpen(true);
              }}
              className={
                canToggle
                  ? 'cursor-pointer inline-flex'
                  : 'inline-flex cursor-not-allowed opacity-50'
              }
            >
              <div style={{ pointerEvents: 'none' }}>
                <ToggleButton
                  checked={row.isExpired ? false : row.status === 'active'}
                  onChange={() => {}}
                  label={
                    row.isExpired
                      ? 'Nieaktywny'
                      : row.status === 'active'
                        ? 'Aktywny'
                        : 'Nieaktywny'
                  }
                  size="Small"
                  disabled={!canToggle}
                />
              </div>
            </div>
          );

          if (!privileges.offerActivateDeactivate) {
            return (
              <Tooltip
                content={OFFER_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                side="left"
                forceShow
                triggerWrapContent
              >
                {statusToggle}
              </Tooltip>
            );
          }

          return statusToggle;
        },
      },
      {
        key: 'actions',
        header: 'Akcje',
        width: '60px',
        // noTruncate: true,
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative inline-flex items-center justify-center w-full"
            >
              <button
                ref={(el) => {
                  if (isOpen) {
                    menuTriggerRef.current = el;
                  }
                }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    menuTriggerRef.current = e.currentTarget;
                    const buttonRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    const spaceBelow = visibleBottom - buttonRect.bottom;
                    setMenuOpensUpward(spaceBelow < minSpaceBelowForMenu);
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={`Akcje dla oferty ${row.offerName}`}
                data-testid={`action-menu-button-offer-${index ?? 0}`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                >
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isOpen && (
                <div
                  className={`absolute right-0 z-50 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab') {
                      return;
                    }
                    const anchor = menuAnchorRef.current;
                    if (!anchor) {
                      return;
                    }
                    const items =
                      anchor.querySelectorAll<HTMLElement>('[role="menuitem"]');
                    const lastItem = items[items.length - 1];
                    if (
                      lastItem &&
                      document.activeElement === lastItem &&
                      !e.shiftKey
                    ) {
                      setOpenMenuRowId(null);
                    }
                  }}
                >
                  <Menu
                    items={[
                      {
                        id: 'VIEW',
                        label: 'Podgląd',
                        disabled: !privileges.offerView,
                        disabledTooltip:
                          OFFER_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
                        disabledTooltipSide: 'left',
                      },
                      {
                        id: 'EDIT',
                        label: 'Edytuj',
                        disabled: !privileges.offerEdit || !!row.isExpired,
                        disabledTooltip: !privileges.offerEdit
                          ? OFFER_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP
                          : 'Ta oferta wygasła i nie można jej edytować.',
                        disabledTooltipSide: 'left',
                      },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(`/Oferty/Podgląd-oferty/${row.id}`, {
                          state: { returnToListSearch },
                        });
                      } else if (itemId === 'EDIT' && !row.isExpired) {
                        navigate(`/Oferty/Edytuj-ofertę/${row.id}`);
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    // offerNameColumnWidthPx and viewportWidth kept for when column width logic is re-enabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      openMenuRowId,
      menuOpensUpward,
      offerNameColumnWidthPx,
      viewportWidth,
      navigate,
      setSelectedOffer,
      setIsDialogOpen,
      returnToListSearch,
      privileges,
    ]
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (col) => col.key === 'actions' || visibleColumnKeys[col.key] !== false
      ),
    [columns, visibleColumnKeys]
  );

  const handleCloseColumnPopup = useCallback(() => {
    setIsColumnPopupOpen(false);
  }, []);

  const handleColumnVisibilityChange = (key: string, visible: boolean) => {
    const nextVisible = { ...visibleColumnKeys, [key]: visible };
    setVisibleColumnKeys(nextVisible);
    const colsString = buildColsParam(nextVisible);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (colsString === DEFAULT_COLS_STRING) {
          next.delete(COLUMNS_URL_PARAM);
        } else {
          next.set(COLUMNS_URL_PARAM, colsString);
        }
        return next;
      },
      { replace: true }
    );
  };

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = useCallback(() => {
    setStatusOptions(appliedFilters.statusOptions);
    setDateRange(appliedFilters.dateRange);
    setSubscriptionPlanOptions((current) => {
      const applied = appliedFilters.subscriptionPlanOptions;
      if (applied.length > 0) {
        return applied;
      }
      return current.map((p) => ({ ...p, checked: false }));
    });
    setIsFilterOpen(false);
  }, [
    appliedFilters.dateRange,
    appliedFilters.statusOptions,
    appliedFilters.subscriptionPlanOptions,
    setDateRange,
    setStatusOptions,
    setSubscriptionPlanOptions,
  ]);

  // Close filter, column popup, or row menu when clicking anywhere outside them
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (
        isFilterOpen &&
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
      if (
        isColumnPopupOpen &&
        columnContainerRef.current &&
        !columnContainerRef.current.contains(target)
      ) {
        handleCloseColumnPopup();
      }
      if (
        openMenuRowId !== null &&
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(target)
      ) {
        setOpenMenuRowId(null);
      }
    };
    if (isFilterOpen || isColumnPopupOpen || openMenuRowId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    isFilterOpen,
    isColumnPopupOpen,
    openMenuRowId,
    handleCloseFilter,
    handleCloseColumnPopup,
  ]);

  const handleDialogClose = () => {
    if (!isSubmitting) {
      setIsDialogOpen(false);
      setSelectedOffer(null);
    }
  };

  const handleConfirmStatusUpdate = async () => {
    if (!selectedOffer) {
      return;
    }
    setIsSubmitting(true);
    const isActive = selectedOffer.status !== 'active';
    try {
      const result = await updateOfferStatusApiService.updateOfferStatus({
        offerId: selectedOffer.id,
        isActive,
      });
      if (result.success) {
        const message = isActive
          ? 'Oferta została aktywowana.'
          : 'Oferta została dezaktywowana.';
        setStatusSuccessMessage(message);
        setShowStatusSuccessSnackbar(true);
        setIsDialogOpen(false);
        setSelectedOffer(null);
        refetchOffers();
      } else {
        setStatusErrorMessage(
          result.error ?? 'Wystąpił błąd podczas aktualizacji statusu.'
        );
        setShowStatusErrorSnackbar(true);
        setIsDialogOpen(false);
        setSelectedOffer(null);
        refetchOffers();
      }
    } catch {
      setStatusErrorMessage(
        'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.'
      );
      setShowStatusErrorSnackbar(true);
      setIsDialogOpen(false);
      setSelectedOffer(null);
      refetchOffers();
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    offers.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !search.trim() &&
    !debouncedSearch.trim() &&
    !appliedFilters.statusOptions.some((o) => o.checked) &&
    !appliedFilters.dateRange.startDate &&
    !appliedFilters.dateRange.endDate &&
    !appliedFilters.subscriptionPlanOptions.some((o) => o.checked);

  // When no data and actively loading or clearing, show shimmer instead of "Brak ofert."
  const showShimmerForEmpty =
    offers.length === 0 && (loading || isSearchClearing);

  if (!privilegesLoading && !privileges.offerList) {
    return <ErrorPage403 />;
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  return (
    <>
      <div className="w-full h-full min-h-0 flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
        <div
          className="flex items-center justify-between shrink-0"
          id="offer-list-content"
          data-testid="offer-list-header"
        >
          <div
            className="text-[#181A1B] text-[22px] font-normal leading-7"
            data-testid="offer-list-title"
          >
            Oferty
          </div>
          {!hasNoData &&
            (privileges.offerCreate ? (
              <button
                onClick={() =>
                  navigate('/Oferty/Utwórz-ofertę', {
                    state: { returnToListSearch },
                  })
                }
                className="h-10 w-[119px] bg-[#2A4455] flex items-center justify-center rounded-lg hover:bg-[#1f3340] transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                style={{ padding: '10px 16px' }}
                aria-label="Utwórz ofertę"
                data-testid="create-offer-button"
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  Utwórz ofertę
                </span>
              </button>
            ) : (
              <Tooltip
                content={OFFER_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                side="left"
                forceShow
                triggerWrapContent
              >
                <button
                  disabled
                  className="h-10 w-[119px] bg-[#B0BEC5] flex items-center justify-center rounded-lg cursor-not-allowed transition-colors"
                  style={{ padding: '10px 16px' }}
                  aria-label="Utwórz ofertę"
                  data-testid="create-offer-button"
                >
                  <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                    Utwórz ofertę
                  </span>
                </button>
              </Tooltip>
            ))}
        </div>

        {!hasNoData && (
          <>
            <div
              className="flex items-center justify-between shrink-0"
              data-testid="offer-list-search-filter"
            >
              <div className="shrink-0 max-w-[312px] w-full sm:w-[280px] md:w-[400px] lg:w-[500px] xl:w-[720px]">
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  placeholder="Szukaj po nazwie"
                />
              </div>
              <div className="flex items-center gap-2">
                <div
                  ref={filterContainerRef}
                  className={`dropdown ${isFilterOpen ? 'show' : ''}`}
                >
                  <style>{`
                    .dropdown { position: relative; }
                    .dropdown.show .dropdown-content { display: block; }
                  `}</style>
                  <FilterBar
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    isOpen={isFilterOpen}
                    filterCount={filterCount}
                    width={97}
                    height={40}
                  />
                  <FilterPopup
                    isOpen={isFilterOpen}
                    onClose={handleCloseFilter}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                    statusOptions={statusOptions}
                    onStatusChange={setStatusOptions}
                    subscriptionPlanOptions={subscriptionPlanOptions}
                    onSubscriptionPlanChange={setSubscriptionPlanOptions}
                    showSubscriptionPlanSection
                    subscriptionPlansLoading={subscriptionPlansLoading}
                    usePlanDropdown
                    onApply={handleApplyFiltersAndClose}
                    onClear={handleClearFilters}
                  />
                </div>
                <div ref={columnContainerRef} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      handleCloseFilter();
                      setIsColumnPopupOpen((prev) => !prev);
                    }}
                    className="flex items-center justify-center rounded-lg border border-[#DCDDE0] px-[8px] py-[8px] cursor-pointer relative overflow-visible transition-all duration-200 ease-in-out hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                    aria-label="Kolumny"
                    aria-expanded={isColumnPopupOpen}
                    aria-haspopup="dialog"
                    data-testid="column-customization-button"
                  >
                    <ViewColumnsIcon
                      className="w-6 h-6 text-on-surface-variant shrink-0"
                      aria-hidden
                    />
                  </button>
                  <ColumnCustomizationPopup
                    isOpen={isColumnPopupOpen}
                    onClose={handleCloseColumnPopup}
                    columnOptions={OFFER_COLUMN_OPTIONS}
                    visibleKeys={visibleColumnKeys}
                    onVisibilityChange={handleColumnVisibilityChange}
                    title="Wybierz kolumny do wyświetlenia"
                    disabledTooltipText="Ta kolumna jest wymagana"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div
          ref={tableScrollContainerRef}
          className="flex flex-col flex-1 min-h-0 overflow-x-auto overflow-y-auto rounded-t"
        >
          {showShimmerForEmpty ? (
            <Table
              columns={visibleColumns}
              data={[]}
              loading
              loadingRows={limit}
              stickyHeader
              // tableContainerStyle={{ width: '100%' }}
              tableBodyStyle={{
                // position: 'relative',
                zIndex: openMenuRowId ? 20 : 11,
              }}
              onSort={handleSort}
              {...(sortBy === 'offer_name'
                ? { sortColumn: 'offerName' as const, sortDirection: sortOrder }
                : sortBy === 'start_date'
                  ? {
                      sortColumn: 'validityPeriod' as const,
                      sortDirection: sortOrder,
                    }
                  : { sortDirection: sortOrder })}
              getRowKey={(row) => row.id}
              getRowClassName={(row) => (row.isExpired ? '!bg-[#F2F4F5]' : '')}
              getStickyCellZIndex={() => 1}
            />
          ) : offers.length === 0 && !loading && !isSearchClearing ? (
            hasNoData ? (
              <div
                className="flex flex-col items-center justify-center w-full h-full"
                data-testid="no-data-state"
              >
                <LottieAnimation
                  animationData={noDataAnimation}
                  width={277}
                  aspectRatio="1 / 1"
                />
                <div className="flex flex-col items-center justify-center gap-5">
                  <div className="flex flex-col items-start justify-start gap-1">
                    <div
                      className="w-[548px] text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]"
                      data-testid="no-data-title"
                    >
                      Brak ofert.
                    </div>
                    <div
                      className="w-[548px] text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]"
                      data-testid="no-data-description"
                    >
                      Utwórz ofertę, aby stosować rabaty do planów subskrypcji.
                    </div>
                  </div>
                  {privileges.offerCreate ? (
                    <button
                      onClick={() =>
                        navigate('/Oferty/Utwórz-ofertę', {
                          state: { returnToListSearch },
                        })
                      }
                      className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center"
                      data-testid="no-data-create-button"
                    >
                      <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                        Utwórz ofertę
                      </span>
                    </button>
                  ) : (
                    <Tooltip
                      content={OFFER_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                      side="bottom"
                      forceShow
                      triggerWrapContent
                    >
                      <button
                        disabled
                        className="h-10 bg-[#B0BEC5] px-4 py-2.5 cursor-not-allowed rounded-lg transition-colors flex items-center justify-center"
                        data-testid="no-data-create-button"
                      >
                        <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                          Utwórz ofertę
                        </span>
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center w-full h-full gap-3"
                data-testid="no-results-state"
              >
                <LottieAnimation
                  animationData={noResultsAnimation}
                  width="clamp(160px, 18vw, 360px)"
                  aspectRatio="432 / 350"
                />
                <div className="w-[548px] max-w-full h-12 flex flex-col items-center justify-start gap-1">
                  <div
                    className="w-full h-6 text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]"
                    data-testid="no-results-title"
                  >
                    Nie znaleziono wyników.
                  </div>
                  <div
                    className="w-full h-5 text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]"
                    data-testid="no-results-description"
                  >
                    {search.trim() && filterCount === 0
                      ? 'Spróbuj zmienić kryteria wyszukiwania.'
                      : !search.trim() && filterCount > 0
                        ? 'Spróbuj zmienić ustawienia filtrów.'
                        : 'Spróbuj zmienić kryteria wyszukiwania lub filtry.'}
                  </div>
                </div>
              </div>
            )
          ) : (
            <Table
              columns={visibleColumns}
              data={offers}
              loading={loading}
              loadingRows={limit}
              stickyHeader
              tableContainerStyle={{ width: '100%' }}
              tableBodyStyle={{
                position: 'relative',
                zIndex: openMenuRowId ? 20 : 11,
              }}
              onSort={handleSort}
              {...(sortBy === 'offer_name'
                ? { sortColumn: 'offerName' as const, sortDirection: sortOrder }
                : sortBy === 'start_date'
                  ? {
                      sortColumn: 'validityPeriod' as const,
                      sortDirection: sortOrder,
                    }
                  : { sortDirection: sortOrder })}
              getRowKey={(row) => row.id}
              getRowClassName={(row) => (row.isExpired ? '!bg-[#F2F4F5]' : '')}
              getStickyCellZIndex={(row, _idx, columnKey) =>
                columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
              }
            />
          )}
        </div>

        {!hasNoData && totalCount > 10 && (
          <div className="px-4 -mx-4 shrink-0">
            <Pagination
              page={page}
              limit={limit}
              totalCount={totalCount}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
            {limitError && (
              <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
                {limitError}
              </div>
            )}
          </div>
        )}

        <Snackbar
          open={showStatusSuccessSnackbar}
          message={statusSuccessMessage}
          variant="success"
          onClose={() => setShowStatusSuccessSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton
        />
        <Snackbar
          open={showStatusErrorSnackbar}
          message={statusErrorMessage}
          variant="error"
          onClose={() => setShowStatusErrorSnackbar(false)}
          autoHideDuration={10000}
          position="top-center"
          showCloseButton
        />

        {selectedOffer && (
          <OfferUpdateStatusDialog
            open={isDialogOpen}
            onClose={handleDialogClose}
            onConfirm={handleConfirmStatusUpdate}
            isSubmitting={isSubmitting}
            offerName={selectedOffer.offerName}
            isActive={selectedOffer.status === 'active'}
          />
        )}
      </div>
    </>
  );
}

export default OfferList;
